/**
 * AutoScroll Plugin
 * Continuous marquee-style scrolling at a constant pixels-per-second rate.
 * Runs its own rAF loop and routes position updates through
 * ctx.commands.driveTrackPosition so TrackAnimator handles
 * loop wrapping and keeps #currentPos in sync.
 *
 * Speed API (signed):
 *   speed < 0  → forward (track moves left)
 *   speed > 0  → backward (track moves right)
 *   speed === 0 → disabled
 */

const DEFAULT_OPTIONS = {
	speed: 0,
	pauseOnHover: true,
	resumeDelay: 3000,
};

export default class AutoScroll {
	static pluginName = 'autoscroll';
	static defaultOptions = { autoscroll: { ...DEFAULT_OPTIONS } };

	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.autoscrollOptions = { ...DEFAULT_OPTIONS };

		_.isScrolling = false;
		_.isSuspended = false;
		_.isPausedByHover = false;
		_.lastFrameTime = 0;
		_.rafId = null;
		_.debouncedResume = null;

		// bound tick for rAF
		_.boundTick = (time) => _.tick(time);

		_.handlers = {
			userInteracted: ({ via }) => {
				if (via === 'hover') return;
				_.pause();
			},

			optionsChanged: () => _.reInit(),

			canLoopChanged: ({ canLoop }) => {
				if (!canLoop) {
					_.stop();
				} else if (_.autoscrollOptions.speed !== 0) {
					_.start();
				}
			},

			layoutChanged: () => {
				_.lastFrameTime = 0;
			},

			windowFocused: () => {
				if (_.isSuspended) _.resume();
			},

			windowBlurred: () => {
				if (_.isScrolling && !_.isSuspended) _.suspend();
			},

			visibilityChanged: ({ hidden }) => {
				if (hidden) {
					if (_.isScrolling && !_.isSuspended) _.suspend();
				} else {
					if (_.isSuspended) _.resume();
				}
			},

			mouseEnter: () => _.handleMouseEnter(),
			mouseLeave: () => _.handleMouseLeave(),
		};

		_.init();
	}

	init() {
		const _ = this;
		_.bindEvents();
		_.reInit();
	}

	reInit() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		_.autoscrollOptions = { ...DEFAULT_OPTIONS, ...(options.autoscroll || {}) };

		// recreate debounced resume with current delay
		if (_.debouncedResume && _.debouncedResume.cancel) {
			_.debouncedResume.cancel();
		}
		const delay = _.autoscrollOptions.resumeDelay;
		if (delay > 0) {
			_.debouncedResume = _.ctx.utils.debounce(() => _.start(), delay);
		} else {
			_.debouncedResume = null;
		}

		// start or stop based on speed
		if (_.autoscrollOptions.speed !== 0) {
			_.start();
		} else {
			_.stop();
		}
	}

	start() {
		const _ = this;
		const { speed } = _.autoscrollOptions;
		const options = _.ctx.store.getOptions();

		if (!speed || speed === 0) return;

		// guard: viewport must have width (layout not ready)
		if (_.ctx.store.getWidths().viewport === 0) return;

		// don't start if loop is enabled but can't actually loop
		const state = _.ctx.store.getState();
		if (options.loop && !state.canLoop) return;

		// guard against autoplay conflict
		if (options.autoplay && options.autoplay.interval) {
			console.warn('AutoScroll: Cannot run alongside autoplay. Disable autoplay.interval first.');
			return;
		}

		_.lastFrameTime = 0;
		_.isScrolling = true;
		_.isSuspended = false;

		// start own rAF loop
		_.scheduleFrame();
	}

	stop() {
		const _ = this;
		_.isScrolling = false;
		_.isSuspended = false;
		_.isPausedByHover = false;
		_.lastFrameTime = 0;
		_.cancelFrame();

		if (_.debouncedResume && _.debouncedResume.cancel) {
			_.debouncedResume.cancel();
		}
	}

	pause() {
		const _ = this;

		// Stop scrolling if active
		if (_.isScrolling) {
			_.isScrolling = false;
			_.lastFrameTime = 0;
			_.cancelFrame();
		}

		// Always refresh the resume timer (even if already paused)
		if (_.debouncedResume && _.autoscrollOptions.speed !== 0) {
			_.debouncedResume();
		}
	}

	suspend() {
		const _ = this;
		_.isSuspended = true;
		_.lastFrameTime = 0;
		_.cancelFrame();
	}

	resume() {
		const _ = this;
		if (!_.isSuspended) return;

		_.isSuspended = false;
		_.lastFrameTime = 0;

		if (_.isScrolling) {
			_.scheduleFrame();
		}
	}

	/** schedule the next rAF tick (no-op if already pending) */
	scheduleFrame() {
		if (this.rafId === null) {
			this.rafId = requestAnimationFrame(this.boundTick);
		}
	}

	/** cancel any pending rAF */
	cancelFrame() {
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}

	/** rAF callback — drives position each frame */
	tick(time) {
		const _ = this;
		_.rafId = null;

		if (!_.isScrolling || _.isSuspended || _.isPausedByHover) return;

		// first frame after start/resume — record time, no movement
		if (_.lastFrameTime === 0) {
			_.lastFrameTime = time;
			_.scheduleFrame();
			return;
		}

		// calculate delta, clamp to 100ms to prevent jumps after tab restore
		const deltaTime = Math.min((time - _.lastFrameTime) / 1000, 0.1);
		_.lastFrameTime = time;

		const { speed } = _.autoscrollOptions;
		const deltaPx = speed * deltaTime;

		// boundary check for non-loop mode
		const state = _.ctx.store.getState();
		if (!state.canLoop) {
			const animation = _.ctx.store.getAnimation();
			const projected = animation.trackPosition + deltaPx;
			const firstSnap = state.firstSnapPoint;
			const lastSnap = state.lastSnapPoint;

			if (speed < 0 && projected <= lastSnap) {
				_.ctx.commands.driveTrackPosition({ deltaPx: lastSnap - animation.trackPosition });
				_.stop();
				return;
			}

			if (speed > 0 && projected >= firstSnap) {
				_.ctx.commands.driveTrackPosition({ deltaPx: firstSnap - animation.trackPosition });
				_.stop();
				return;
			}
		}

		_.ctx.commands.driveTrackPosition({ deltaPx, velocity: speed });

		// schedule next frame
		_.scheduleFrame();
	}

	handleMouseEnter() {
		const _ = this;
		if (!_.autoscrollOptions.pauseOnHover || !_.isScrolling) return;

		_.isPausedByHover = true;
		_.lastFrameTime = 0;
		_.cancelFrame();
	}

	handleMouseLeave() {
		const _ = this;
		if (!_.autoscrollOptions.pauseOnHover || !_.isPausedByHover) return;

		_.isPausedByHover = false;
		_.lastFrameTime = 0;

		if (_.isScrolling) {
			_.scheduleFrame();
		}
	}

	bindEvents() {
		const _ = this;
		const { emitter, events } = _.ctx;

		emitter.on(events.user.interacted, _.handlers.userInteracted);
		emitter.on(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.on(events.store.canLoopChanged, _.handlers.canLoopChanged);
		emitter.on(events.store.layoutChanged, _.handlers.layoutChanged);
		emitter.on(events.window.hasFocus, _.handlers.windowFocused);
		emitter.on(events.window.lostFocus, _.handlers.windowBlurred);
		emitter.on(events.window.visibilityChange, _.handlers.visibilityChanged);

		// DOM events for hover pause
		_.ctx.carousel.addEventListener('mouseenter', _.handlers.mouseEnter);
		_.ctx.carousel.addEventListener('mouseleave', _.handlers.mouseLeave);
	}

	destroy() {
		const _ = this;

		_.stop();

		const { emitter, events } = _.ctx;

		emitter.off(events.user.interacted, _.handlers.userInteracted);
		emitter.off(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.off(events.store.canLoopChanged, _.handlers.canLoopChanged);
		emitter.off(events.store.layoutChanged, _.handlers.layoutChanged);
		emitter.off(events.window.hasFocus, _.handlers.windowFocused);
		emitter.off(events.window.lostFocus, _.handlers.windowBlurred);
		emitter.off(events.window.visibilityChange, _.handlers.visibilityChanged);

		_.ctx.carousel.removeEventListener('mouseenter', _.handlers.mouseEnter);
		_.ctx.carousel.removeEventListener('mouseleave', _.handlers.mouseLeave);

		if (_.debouncedResume && _.debouncedResume.cancel) {
			_.debouncedResume.cancel();
		}
	}
}

// Queue the plugin (works regardless of whether Tarot is loaded)
(window.TarotPluginQueue = window.TarotPluginQueue || []).push({
	pluginName: 'autoscroll',
	plugin: AutoScroll
});
